import { horoscopeContent } from '@/data/horoscopeContent';

export const ZODIAC_SIGNS = [
  { slug: 'aries', name: 'Aries', symbol: '♈', dates: 'Mar 21 - Apr 19', element: 'Fire' },
  { slug: 'taurus', name: 'Taurus', symbol: '♉', dates: 'Apr 20 - May 20', element: 'Earth' },
  { slug: 'gemini', name: 'Gemini', symbol: '♊', dates: 'May 21 - Jun 20', element: 'Air' },
  { slug: 'cancer', name: 'Cancer', symbol: '♋', dates: 'Jun 21 - Jul 22', element: 'Water' },
  { slug: 'leo', name: 'Leo', symbol: '♌', dates: 'Jul 23 - Aug 22', element: 'Fire' },
  { slug: 'virgo', name: 'Virgo', symbol: '♍', dates: 'Aug 23 - Sep 22', element: 'Earth' },
  { slug: 'libra', name: 'Libra', symbol: '♎', dates: 'Sep 23 - Oct 22', element: 'Air' },
  { slug: 'scorpio', name: 'Scorpio', symbol: '♏', dates: 'Oct 23 - Nov 21', element: 'Water' },
  {
    slug: 'sagittarius',
    name: 'Sagittarius',
    symbol: '♐',
    dates: 'Nov 22 - Dec 21',
    element: 'Fire',
  },
  {
    slug: 'capricorn',
    name: 'Capricorn',
    symbol: '♑',
    dates: 'Dec 22 - Jan 19',
    element: 'Earth',
  },
  { slug: 'aquarius', name: 'Aquarius', symbol: '♒', dates: 'Jan 20 - Feb 18', element: 'Air' },
  { slug: 'pisces', name: 'Pisces', symbol: '♓', dates: 'Feb 19 - Mar 20', element: 'Water' },
] as const;

export type ZodiacSignSlug = (typeof ZODIAC_SIGNS)[number]['slug'];
export type ZodiacSignName = (typeof ZODIAC_SIGNS)[number]['name'];
export type ZodiacMeta = (typeof ZODIAC_SIGNS)[number];

export type DailyHoroscopeSnapshot = {
  overview: string;
  focus: string;
  caution: string;
  opportunity: string;
  love: string;
  work: string;
  ritual: string;
  luckyNumbers: number[];
  luckyDay: string;
  compatibilitySign: string;
};

export type WeeklyHoroscopeReading = {
  sunSign: string;
  weekOf: string;
  overview: string;
  love: string;
  career: string;
  finance: string;
  luckyNumbers: number[];
  luckyDay: string;
  compatibility: string;
};

export type PublicHoroscopeReading = DailyHoroscopeSnapshot & {
  sunSign: ZodiacSignName;
  weekOf: string;
  career: string;
  finance: string;
  compatibility: string;
  source: 'backend' | 'fallback';
};

const ELEMENT_OPENERS = {
  Fire: [
    'Momentum is easier to trust when you point it somewhere precise.',
    'Today responds well to brave action, but only after your intention is named clearly.',
    'Your energy rises fastest when urgency is matched with purpose.',
  ],
  Earth: [
    'Steady progress matters more than dramatic change today.',
    'The strongest move is the one that protects future stability without freezing the present.',
    'Consistency does more for you today than speed ever could.',
  ],
  Air: [
    'Clarity comes through naming what matters, not through collecting every possible option.',
    'The best insight today arrives when conversation turns practical.',
    'You make stronger decisions today when ideas are sorted into one clear priority.',
  ],
  Water: [
    'Sensitivity is useful today when it is paired with a clean boundary.',
    'Emotional weather says something real today, but it does not need to run the whole day.',
    'Softness works best today when you stay honest about what you actually need.',
  ],
} as const;

const FOCUS_TEMPLATES = [
  'Protect the strongest part of your day for one meaningful decision.',
  'Choose one task that proves your priorities instead of talking around them.',
  'Return to the habit that usually steadies you when your attention scatters.',
  'Say the important thing earlier than you normally would.',
];

const CAUTION_TEMPLATES = [
  'Do not confuse urgency with truth.',
  'Avoid overpromising before your schedule has room for it.',
  'Watch for the old habit of reacting before context has fully arrived.',
  'Keep one eye on energy, not just output.',
];

const OPPORTUNITY_TEMPLATES = [
  'A small course correction today compounds better than a dramatic reset later.',
  'There is leverage in one honest conversation.',
  'You gain more by refining what is already working than by starting over.',
  'A practical next step reveals more than another round of theory.',
];

const LOVE_TEMPLATES = [
  'In relationships, directness lands best when it is paired with reassurance.',
  'Love goes better today when you show consistency instead of waiting to feel perfectly ready.',
  'Connection strengthens when you ask for clarity instead of silently testing it.',
  'Affection feels more real today when it becomes visible through follow-through.',
];

const WORK_TEMPLATES = [
  'At work, choose the lane where your natural strengths create calm for others.',
  'Professional momentum improves when you define the finish line before the meeting starts.',
  'Your best work today comes from trimming noise, not adding more effort.',
  'A useful result beats a polished overbuild today.',
];

const RITUAL_TEMPLATES = [
  'Take two minutes tonight to write one signal you want to remember tomorrow.',
  'Use a short reset between tasks so your mood does not drive your next decision.',
  'End the day by naming one thing that worked and one thing that needs less force.',
  'Give yourself one quiet pause before responding to the most emotionally loaded message.',
];

const LUCKY_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function pickByIndex<T>(items: readonly T[], seed: number) {
  return items[seed % items.length];
}

function numericSeed(sign: ZodiacSignSlug, date: Date) {
  const signIndex = ZODIAC_SIGNS.findIndex((item) => item.slug === sign);
  return signIndex + date.getUTCDate() + (date.getUTCMonth() + 1) * 3 + date.getUTCFullYear();
}

function getHoroscopeApiBaseUrl() {
  return process.env.API_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
}

function buildFallbackWeekly(sign: ZodiacSignSlug, date = new Date()): WeeklyHoroscopeReading {
  const snapshot = getDailyHoroscopeSnapshot(sign, date);
  const meta = getZodiacMeta(sign);

  return {
    sunSign: meta?.name || 'Unknown',
    weekOf: getHoroscopeDateLabel(date),
    overview: snapshot.overview,
    love: snapshot.love,
    career: snapshot.work,
    finance: snapshot.opportunity,
    luckyNumbers: snapshot.luckyNumbers,
    luckyDay: snapshot.luckyDay,
    compatibility: snapshot.compatibilitySign,
  };
}

function buildPublicReading(
  sign: ZodiacSignSlug,
  weekly: WeeklyHoroscopeReading,
  source: PublicHoroscopeReading['source'],
  date = new Date(),
): PublicHoroscopeReading {
  const snapshot = getDailyHoroscopeSnapshot(sign, date);
  const normalizedName = normalizeZodiacSignName(weekly.sunSign);

  return {
    ...snapshot,
    sunSign: normalizedName || getZodiacMeta(sign)?.name || 'Aries',
    weekOf: weekly.weekOf,
    overview: weekly.overview || snapshot.overview,
    love: weekly.love || snapshot.love,
    work: weekly.career || snapshot.work,
    career: weekly.career || snapshot.work,
    finance: weekly.finance || snapshot.opportunity,
    compatibility: weekly.compatibility || snapshot.compatibilitySign,
    compatibilitySign: weekly.compatibility || snapshot.compatibilitySign,
    luckyNumbers:
      weekly.luckyNumbers && weekly.luckyNumbers.length > 0
        ? weekly.luckyNumbers
        : snapshot.luckyNumbers,
    luckyDay: weekly.luckyDay || snapshot.luckyDay,
    source,
  };
}

function isValidWeeklyHoroscope(payload: unknown): payload is WeeklyHoroscopeReading {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const value = payload as Partial<WeeklyHoroscopeReading>;
  return Boolean(
    value.sunSign &&
      value.weekOf &&
      value.overview &&
      value.love &&
      value.career &&
      value.finance &&
      value.luckyDay &&
      value.compatibility &&
      Array.isArray(value.luckyNumbers),
  );
}

export function getHoroscopeDateLabel(date = new Date()) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getZodiacMeta(sign: ZodiacSignSlug) {
  return ZODIAC_SIGNS.find((item) => item.slug === sign);
}

export function normalizeZodiacSignName(value?: string | null): ZodiacSignName | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  const match = ZODIAC_SIGNS.find(
    (item) => item.slug === normalized || item.name.toLowerCase() === normalized,
  );

  return match?.name || null;
}

export function normalizeZodiacSlug(value?: string | null): ZodiacSignSlug | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  const match = ZODIAC_SIGNS.find(
    (item) => item.slug === normalized || item.name.toLowerCase() === normalized,
  );

  return match?.slug || null;
}

export function getZodiacSlugFromMonthDay(month: number, day: number): ZodiacSignSlug {
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'aries';
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'taurus';
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'gemini';
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'cancer';
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'leo';
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'virgo';
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'libra';
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'scorpio';
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'sagittarius';
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 'capricorn';
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'aquarius';
  return 'pisces';
}

export function getZodiacSlugFromBirthDate(birthDate?: string | null): ZodiacSignSlug | null {
  if (!birthDate) {
    return null;
  }

  const match = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!month || !day || month > 12 || day > 31) {
    return null;
  }

  return getZodiacSlugFromMonthDay(month, day);
}

export function getZodiacNameFromBirthDate(birthDate?: string | null): ZodiacSignName | null {
  const slug = getZodiacSlugFromBirthDate(birthDate);
  return slug ? getZodiacMeta(slug)?.name || null : null;
}

export function getDailyHoroscopeSnapshot(
  sign: ZodiacSignSlug,
  date = new Date(),
): DailyHoroscopeSnapshot {
  const meta = getZodiacMeta(sign);
  const signInfo = horoscopeContent[sign];
  const seed = numericSeed(sign, date);
  const signIndex = ZODIAC_SIGNS.findIndex((item) => item.slug === sign);
  const compatibilitySign = ZODIAC_SIGNS[(signIndex + (seed % 5) + 1) % ZODIAC_SIGNS.length].name;
  const luckyNumbers = [0, 1, 2].map((offset) => ((seed + offset * 7) % 36) + 1);

  if (!meta) {
    return {
      overview: signInfo.growth,
      focus: signInfo.strengths,
      caution: signInfo.blindSpots,
      opportunity: signInfo.work,
      love: signInfo.love,
      work: signInfo.work,
      ritual: signInfo.growth,
      luckyNumbers,
      luckyDay: pickByIndex(LUCKY_DAYS, seed),
      compatibilitySign,
    };
  }

  const opener = pickByIndex(ELEMENT_OPENERS[meta.element], seed);
  return {
    overview: `${opener} ${signInfo.growth}`,
    focus: `${pickByIndex(FOCUS_TEMPLATES, seed + 1)} ${signInfo.strengths}`,
    caution: `${pickByIndex(CAUTION_TEMPLATES, seed + 2)} ${signInfo.blindSpots}`,
    opportunity: `${pickByIndex(OPPORTUNITY_TEMPLATES, seed + 3)} ${signInfo.work}`,
    love: `${pickByIndex(LOVE_TEMPLATES, seed + 4)} ${signInfo.love}`,
    work: `${pickByIndex(WORK_TEMPLATES, seed + 5)} ${signInfo.work}`,
    ritual: `${pickByIndex(RITUAL_TEMPLATES, seed + 6)} ${signInfo.growth}`,
    luckyNumbers,
    luckyDay: pickByIndex(LUCKY_DAYS, seed),
    compatibilitySign,
  };
}

export async function getPublicHoroscopeReading(
  sign: ZodiacSignSlug,
  date = new Date(),
): Promise<PublicHoroscopeReading> {
  const fallback = buildFallbackWeekly(sign, date);

  try {
    const response = await fetch(`${getHoroscopeApiBaseUrl()}/horoscope/public/${encodeURIComponent(sign)}`, {
      signal: AbortSignal.timeout(1500),
    });

    if (!response.ok) {
      return buildPublicReading(sign, fallback, 'fallback', date);
    }

    const payload: unknown = await response.json();
    if (!isValidWeeklyHoroscope(payload)) {
      return buildPublicReading(sign, fallback, 'fallback', date);
    }

    return buildPublicReading(sign, payload, 'backend', date);
  } catch {
    return buildPublicReading(sign, fallback, 'fallback', date);
  }
}
